import { createContext, use, useEffect, useState } from "react";

// Import directly from the cache module (which is context-free) rather than the
// barrel: the barrel also re-exports the hook, which imports this context, so
// going through it would form a Context -> barrel -> hook -> Context cycle.
import {
  createSchemaDefinitionSchemaStore,
  SchemaDefinitionSchemaStore,
} from "shared/schemas/schemaDefinitionSchema/schemaDefinitionSchemaCache";

import { ContextWrapComponentProps } from "./types";

// Carries the app-level schema-definition cache to the tree. There is
// deliberately no default store: a consumer rendered without the provider is a
// bug, not a reason to silently share a module-level singleton (which would
// also bleed cache state across tests in the same Vitest worker). The hook
// below throws when the provider is missing so the mistake surfaces early.
const SchemaDefinitionCacheContext = createContext<
  SchemaDefinitionSchemaStore | undefined
>(undefined);

export const SchemaDefinitionCacheContextWrapper = ({
  children,
}: ContextWrapComponentProps) => {
  // One cache instance per provider lifecycle (lazy-initialized once). The
  // provider lives inside the authenticated context tree (ContextWrappers), so
  // the cache is created on login and torn down on logout — no module-level
  // state outliving the session or leaking between tests.
  const [store] = useState(createSchemaDefinitionSchemaStore);

  // Clear the cache when this provider unmounts (logout). Tenant switches reload
  // the page (new hostname), which discards the instance entirely.
  useEffect(() => () => store.clear(), [store]);

  return (
    <SchemaDefinitionCacheContext.Provider value={store}>
      {children}
    </SchemaDefinitionCacheContext.Provider>
  );
};

export const useSchemaDefinitionCacheStore =
  (): SchemaDefinitionSchemaStore => {
    const store = use(SchemaDefinitionCacheContext);
    if (!store) {
      throw new Error(
        "useSchemaDefinitionCacheStore must be used within a SchemaDefinitionCacheContextWrapper"
      );
    }

    return store;
  };
